import FeatureFlagWebClient, {
  type AnalyticsClientInterface,
  type ClientOptions,
  EnvironmentType,
  Identifiers,
  ReadyReason,
} from '@atlassiansox/feature-flag-web-client';

import { BaseError, type BaseErrorOptions } from '@canvas/error';
import { CustomerImpact, captureException, CapabilityTier } from '@canvas/sentry-react';

import {
  type ClientFeatureFlags,
  currentFeatureFlagState,
  FeatureFlagSource,
} from './client-feature-flags';
import { type FeatureFlagOverrides } from './get-feature-flags-from-url';

const environment = ((): EnvironmentType => {
  switch (process.env.VITE_CANVAS_FRONTEND_FEATURE_FLAGS_ENV) {
    case 'dev': {
      return EnvironmentType.DEV;
    }
    case 'staging': {
      return EnvironmentType.STAGING;
    }
    case 'prod': {
      return EnvironmentType.PROD;
    }
    default: {
      return EnvironmentType.DEV;
    }
  }
})();

class LaunchDarklyInitializationError extends BaseError {
  constructor(cause: unknown, options?: BaseErrorOptions) {
    super(
      {
        name: 'LaunchDarklyInitializationError',
        ...options,
      },
      `Failed to initialize LaunchDarkly. Reason: ${LaunchDarklyInitializationError.getReason(
        cause
      )}`
    );
  }

  static getReason(cause: unknown) {
    if (cause instanceof Error) {
      return cause.message;
    } else if (typeof cause === 'string') {
      return cause;
    } else {
      return JSON.stringify(cause);
    }
  }
}

const options: ClientOptions = {
  environment,
  productKey: 'canvas',
  loggerOptions: {
    enabled: environment === EnvironmentType.STAGING,
  },
  readyTimeoutInMilliseconds: 5000,
};

export const clientFeatureFlagsFactory = (flags: ClientFeatureFlags) => {
  let accountId: string;
  let analyticsClient: AnalyticsClientInterface;
  let cloudId: string;

  let initializing = false;
  let initializingPromise: Promise<void>;

  let client: FeatureFlagWebClient;

  const initializer = {
    supplyAccountId: (suppliedAccountId: string) => {
      accountId = suppliedAccountId;
    },
    supplyAnalyticsClient: (suppliedAnalyticsClient: AnalyticsClientInterface) => {
      analyticsClient = suppliedAnalyticsClient;
    },
    supplyCloudId: (suppliedCloudId: string) => {
      cloudId = suppliedCloudId;
    },
    initialize: (featureFlagOverrides: FeatureFlagOverrides) => {
      if (!analyticsClient || !accountId || !cloudId) {
        throw Error(`Not ready to be initialized yet`);
      }

      if (initializing) {
        return initializingPromise;
      }

      initializing = true;
      initializingPromise = initializer.initializeClient(featureFlagOverrides);

      return initializingPromise;
    },
    initializeClient: async (featureFlagOverrides: FeatureFlagOverrides) => {
      try {
        const key = process.env.VITE_CANVAS_FRONTEND_FEATURE_FLAGS_API_KEY;
        if (!key) {
          return;
        }

        const featureFlagClient = new FeatureFlagWebClient(
          key,
          analyticsClient,
          {
            identifier: {
              type: Identifiers.ATLASSIAN_ACCOUNT_ID,
              value: accountId,
            },
            additionalIdentifiers: {
              [Identifiers.TENANT_ID]: cloudId,
            },
          },
          options
        );

        const readyResonse = await featureFlagClient.ready();
        client = featureFlagClient;

        if (
          [ReadyReason.CLIENT_ERROR, ReadyReason.SERVER_ERROR].includes(
            readyResonse.reason
          )
        ) {
          throw new LaunchDarklyInitializationError(
            readyResonse.message ?? 'No reason provided'
          );
        }

        initializer.initializeFeatureFlagValues(featureFlagOverrides);
      } catch (err) {
        captureException(err, {
          tags: {
            captureLocation: 'clientFeatureFlagsFactory-catch-all',
            /**
             * Users won't get our intended FF state. This could affect our rollout timelines but
             * ultimately, our default FF should always be safe for customers to be able to use.
             */
            customerImpact: CustomerImpact.ATLASSIAN,
            /**
             * This can technically have an effect on _any_ capability depending on what features we're rolling out
             * ATM so set this to C2 for now
             */
            capabilityTier: CapabilityTier.C2,
          },
        });
      }
    },
    initializeFeatureFlagValues: (featureFlagOverrides: FeatureFlagOverrides) => {
      Object.entries(flags).forEach(([property, featureFlag]) => {
        const key = property as keyof typeof flags;

        if (featureFlag.source === FeatureFlagSource.DEPRECATED_LaunchDarkly) {
          currentFeatureFlagState[key].enabled =
            featureFlagOverrides[key] !== undefined
              ? featureFlagOverrides[key]
              : client.getFlagValue(featureFlag.flag, false);
        }
      });
    },
  };

  return initializer;
};
